import { computed, shallowRef, type ComputedRef, type Ref } from "vue";
import { assertNever } from "../lib/assertNever";
import { isNothing } from "../lib/maybe";
import { readStringPref, writeStringPref } from "../lib/localPrefs";
import { dispatchInto } from "../lib/tea";
import {
  clampUiZoomPercent,
  initialUiZoomModel,
  UI_ZOOM_DEFAULT_PERCENT,
  UI_ZOOM_MAX_PERCENT,
  UI_ZOOM_MIN_PERCENT,
  updateUiZoom,
  type UiZoomCmd,
  type UiZoomModel,
} from "./useUiZoom.update";

function readPercent(): number {
  const raw = readStringPref("uiZoom");
  if (isNothing(raw)) return UI_ZOOM_DEFAULT_PERCENT;
  const parsed = Number.parseInt(raw.value, 10);
  if (Number.isFinite(parsed) === false) return UI_ZOOM_DEFAULT_PERCENT;
  // One-shot: old builds defaulted to 110. Don't remap repeatedly. That
  // ratcheted zoom down toward 80% on every initUiZoom()/page load.
  if (parsed === 110) {
    writeStringPref("uiZoom", String(UI_ZOOM_DEFAULT_PERCENT));
    return UI_ZOOM_DEFAULT_PERCENT;
  }
  return clampUiZoomPercent(parsed);
}

const holder = shallowRef<UiZoomModel>(initialUiZoomModel(readPercent()));
let applied = false;

function runUiZoomCmd(cmd: UiZoomCmd): void {
  switch (cmd.kind) {
    case "applyPercent":
      if (typeof document !== "undefined") {
        document.documentElement.style.setProperty(
          "--zw-ui-scale",
          String(cmd.percent / 100),
        );
      }
      writeStringPref("uiZoom", String(cmd.percent));
      applied = true;
      return;
    default:
      assertNever(cmd as never);
  }
}

function dispatchZoom(msg: Parameters<typeof updateUiZoom>[1]): void {
  void dispatchInto(holder, msg, updateUiZoom, runUiZoomCmd);
}

/** Apply stored zoom early (call from app mount; index.html also boots it). */
export function initUiZoom(): void {
  if (applied) {
    // Keep the CSS var in sync without re-reading/rewriting storage.
    if (typeof document !== "undefined") {
      document.documentElement.style.setProperty(
        "--zw-ui-scale",
        String(holder.value.percent / 100),
      );
    }
    return;
  }
  dispatchZoom({ type: "SetPercent", percent: readPercent() });
}

export function zoomIn(): void {
  runZoomAction(() => dispatchZoom({ type: "ZoomIn" }));
}

export function zoomOut(): void {
  runZoomAction(() => dispatchZoom({ type: "ZoomOut" }));
}

export function resetZoom(): void {
  runZoomAction(() => dispatchZoom({ type: "Reset" }));
}

/** Menu accelerators and window keydown can both fire; ignore the twin. */
let lastZoomActionAt = 0;
function runZoomAction(action: () => void): void {
  const now = Date.now();
  if (now - lastZoomActionAt < 200) return;
  lastZoomActionAt = now;
  action();
}

/** Browser-style Mod+/−/0 for interface zoom. */
export function installUiZoomShortcuts(): () => void {
  const onKeydown = (event: KeyboardEvent): void => {
    const mod = event.metaKey === true || event.ctrlKey === true;
    if (mod === false || event.altKey === true) return;
    const key = event.key.toLowerCase();
    if (key === "=" || key === "+") {
      event.preventDefault();
      zoomIn();
      return;
    }
    if (key === "-" || key === "_") {
      event.preventDefault();
      zoomOut();
      return;
    }
    if (key === "0") {
      event.preventDefault();
      resetZoom();
    }
  };
  window.addEventListener("keydown", onKeydown);
  return () => window.removeEventListener("keydown", onKeydown);
}

export function useUiZoom(): {
  percent: Ref<number>;
  scale: ComputedRef<number>;
  label: ComputedRef<string>;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  minPercent: number;
  maxPercent: number;
  defaultPercent: number;
} {
  initUiZoom();

  const percent = computed(() => holder.value.percent);
  const label = computed(() => `${holder.value.percent}%`);
  const scale = computed(() => holder.value.percent / 100);

  return {
    percent,
    scale,
    label,
    zoomIn,
    zoomOut,
    resetZoom,
    minPercent: UI_ZOOM_MIN_PERCENT,
    maxPercent: UI_ZOOM_MAX_PERCENT,
    defaultPercent: UI_ZOOM_DEFAULT_PERCENT,
  };
}
